import React, { useState } from 'react';
import { 
  MessageSquare, Send, Users, Clock, AlertTriangle, Shield, 
  Filter, Search, MoreVertical, Eye, Trash2, Flag, CheckCircle, 
  XCircle, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight,
  Calendar, Star, Heart, Smile, Download, RefreshCw, Settings
} from 'lucide-react';

const Messages = () => {
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [actionDropdownId, setActionDropdownId] = useState(null);

  // Mock data - in real app this would come from API
  const messageMetrics = [
    {
      title: 'Total Messages',
      value: '45,892',
      change: '+18.3%',
      trend: 'up',
      icon: MessageSquare,
      color: 'blue'
    },
    {
      title: 'Response Rate',
      value: '76.4%',
      change: '+5.2%',
      trend: 'up',
      icon: TrendingUp,
      color: 'green'
    },
    {
      title: 'Active Conversations',
      value: '2,847',
      change: '-2.1%',
      trend: 'down',
      icon: Users,
      color: 'purple'
    },
    {
      title: 'Avg Response Time',
      value: '2.3h',
      change: '-12.5%',
      trend: 'up',
      icon: Clock,
      color: 'orange'
    }
  ];

  const flaggedMessages = [
    {
      id: 1,
      user: { name: 'John Doe', avatar: 'JD', age: 28 },
      recipient: { name: 'Sarah Smith', avatar: 'SS', age: 25 },
      message: 'Hey beautiful, want to meet up tonight? 😘',
      reason: 'Inappropriate content',
      timestamp: '2 hours ago',
      status: 'pending'
    },
    {
      id: 2,
      user: { name: 'Mike Johnson', avatar: 'MJ', age: 32 },
      recipient: { name: 'Emma Davis', avatar: 'ED', age: 27 },
      message: 'You look amazing in your profile pic!',
      reason: 'Spam',
      timestamp: '4 hours ago',
      status: 'reviewed'
    },
    {
      id: 3,
      user: { name: 'Alex Chen', avatar: 'AC', age: 29 },
      recipient: { name: 'Jessica Kim', avatar: 'JK', age: 26 },
      message: 'Hi there! How are you doing today?',
      reason: 'Suspicious activity',
      timestamp: '6 hours ago',
      status: 'pending'
    }
  ];

  // Add more mock conversations for pagination demo
  const baseConversations = [
    {
      id: 1,
      user1: { name: 'Sarah Johnson', avatar: 'SJ', age: 28, online: true },
      user2: { name: 'Mike Chen', avatar: 'MC', age: 30, online: false },
      lastMessage: 'That sounds great! Looking forward to it 😊',
      messageCount: 45,
      lastActivity: '2 hours ago',
      status: 'active'
    },
    {
      id: 2,
      user1: { name: 'Emma Davis', avatar: 'ED', age: 25, online: true },
      user2: { name: 'Alex Rodriguez', avatar: 'AR', age: 27, online: true },
      lastMessage: 'What time works best for you?',
      messageCount: 23,
      lastActivity: '1 hour ago',
      status: 'active'
    },
    {
      id: 3,
      user1: { name: 'Jessica Kim', avatar: 'JK', age: 29, online: false },
      user2: { name: 'David Wilson', avatar: 'DW', age: 31, online: false },
      lastMessage: 'Thanks for the great conversation!',
      messageCount: 67,
      lastActivity: '1 day ago',
      status: 'completed'
    },
    {
      id: 4,
      user1: { name: 'Amanda Lee', avatar: 'AL', age: 26, online: true },
      user2: { name: 'Chris Thompson', avatar: 'CT', age: 28, online: false },
      lastMessage: 'Hi! How are you doing?',
      messageCount: 12,
      lastActivity: '3 hours ago',
      status: 'active'
    }
  ];
  // Generate more for demo
  const recentConversations = Array.from({ length: 40 }, (_, i) => {
    const base = baseConversations[i % baseConversations.length];
    return {
      ...base,
      id: i + 1,
      user1: { ...base.user1, name: base.user1.name + ' ' + (i + 1) },
      user2: { ...base.user2, name: base.user2.name + ' ' + (i + 1) },
      lastActivity: `${(i % 24) + 1} hours ago`,
    };
  });

  const messageInsights = [
    { topic: 'First impressions', count: 1247, trend: '+15%' },
    { topic: 'Shared interests', count: 892, trend: '+8%' },
    { topic: 'Meeting plans', count: 654, trend: '+12%' },
    { topic: 'Personal stories', count: 543, trend: '+5%' },
    { topic: 'Compliments', count: 432, trend: '+18%' }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 dark:text-green-400';
      case 'completed': return 'text-blue-600 dark:text-blue-400';
      case 'pending': return 'text-yellow-600 dark:text-yellow-400';
      case 'reviewed': return 'text-gray-600 dark:text-gray-400';
      default: return 'text-gray-600 dark:text-gray-400';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <CheckCircle className="w-4 h-4" />;
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'reviewed': return <Eye className="w-4 h-4" />;
      default: return <XCircle className="w-4 h-4" />;
    }
  };

  // --- Filtering, Searching, Pagination Logic ---
  const filteredConversations = recentConversations.filter((conv) => {
    // Filter by status
    if (selectedFilter !== 'all' && conv.status !== selectedFilter) return false;
    // Search by user names or last message
    const q = searchQuery.toLowerCase();
    if (
      !(
        conv.user1.name.toLowerCase().includes(q) ||
        conv.user2.name.toLowerCase().includes(q) ||
        conv.lastMessage.toLowerCase().includes(q)
      )
    ) {
      return false;
    }
    return true;
  });

  // Pagination
  const totalItems = filteredConversations.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const paginatedConversations = filteredConversations.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Handle page change
  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  // Reset to page 1 when filter/search/limit changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [selectedFilter, searchQuery, itemsPerPage]);

  // Dropdown actions for each row
  const handleActionClick = (id) => {
    setActionDropdownId(actionDropdownId === id ? null : id);
  };
  const closeDropdown = () => setActionDropdownId(null);

  // For closing dropdown on outside click
  React.useEffect(() => {
    const handleClick = (e) => {
      if (!e.target.closest('.action-dropdown')) setActionDropdownId(null);
    };
    if (actionDropdownId !== null) {
      document.addEventListener('mousedown', handleClick);
      return () => document.removeEventListener('mousedown', handleClick);
    }
  }, [actionDropdownId]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Message Management</h1>
          <p className="text-gray-600 dark:text-gray-400">Monitor and manage user conversations and messages</p>
        </div>
        <div className="flex items-center gap-3">
          <button className="flex items-center gap-2 px-4 py-2 bg-pink-600 hover:bg-pink-700 text-white rounded-lg transition-colors">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {messageMetrics.map((metric, index) => {
          const Icon = metric.icon;
          return (
            <div key={index} className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{metric.title}</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{metric.value}</p>
                </div>
                <div className={`p-3 rounded-lg bg-${metric.color}-100 dark:bg-${metric.color}-900/20`}>
                  <Icon className={`w-6 h-6 text-${metric.color}-600 dark:text-${metric.color}-400`} />
                </div>
              </div>
              <div className="flex items-center mt-4">
                {metric.trend === 'up' ? (
                  <ArrowUpRight className="w-4 h-4 text-green-600 dark:text-green-400" />
                ) : (
                  <ArrowDownRight className="w-4 h-4 text-red-600 dark:text-red-400" />
                )}
                <span className={`text-sm font-medium ml-1 ${
                  metric.trend === 'up' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                }`}>
                  {metric.change}
                </span>
                <span className="text-sm text-gray-600 dark:text-gray-400 ml-2">vs last period</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Flagged Messages */}
        <div className="lg:col-span-1">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Flagged Messages</h3>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-1 bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-xs font-medium rounded-full">
                    {flaggedMessages.length}
                  </span>
                  <button className="p-2 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700">
                    <MoreVertical className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
            <div className="p-6 space-y-4">
              {flaggedMessages.map((message) => (
                <div key={message.id} className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <div className="w-8 h-8 bg-gradient-to-r from-pink-500 to-purple-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
                        {message.user.avatar}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{message.user.name}</p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">to {message.recipient.name}</p>
                      </div>
                    </div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{message.timestamp}</span>
                  </div>
                  <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">{message.message}</p>
                  <div className="flex items-center justify-between">
                    <span className="px-2 py-1 bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-xs font-medium rounded-full">
                      {message.reason}
                    </span>
                    <div className="flex items-center gap-2">
                      <button className="p-1 rounded text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/20">
                        <CheckCircle className="w-4 h-4" />
                      </button>
                      <button className="p-1 rounded text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/20">
                        <XCircle className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Recent Conversations */}
        <div className="lg:col-span-2">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Conversations</h3>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search conversations..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                    />
                  </div>
                  <select
                    value={selectedFilter}
                    onChange={(e) => setSelectedFilter(e.target.value)}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  >
                    <option value="all">All Conversations</option>
                    <option value="active">Active</option>
                    <option value="completed">Completed</option>
                    <option value="flagged">Flagged</option>
                  </select>
                  <select
                    value={itemsPerPage}
                    onChange={e => setItemsPerPage(Number(e.target.value))}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  >
                    {[10, 20, 30, 50, 100].map(limit => (
                      <option key={limit} value={limit}>{limit} per page</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Users</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Last Message</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Messages</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Last Activity</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {paginatedConversations.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">No conversations found.</td>
                    </tr>
                  ) : (
                    paginatedConversations.map((conversation) => (
                      <tr key={conversation.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-3">
                            <div className="flex -space-x-2">
                              <div className="relative">
                                <div className="w-8 h-8 bg-gradient-to-r from-pink-500 to-purple-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
                                  {conversation.user1.avatar}
                                </div>
                                {conversation.user1.online && (
                                  <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 border-2 border-white dark:border-gray-800 rounded-full"></div>
                                )}
                              </div>
                              <div className="relative">
                                <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
                                  {conversation.user2.avatar}
                                </div>
                                {conversation.user2.online && (
                                  <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-500 border-2 border-white dark:border-gray-800 rounded-full"></div>
                                )}
                              </div>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900 dark:text-white">
                                {conversation.user1.name} & {conversation.user2.name}
                              </p>
                              <p className="text-sm text-gray-600 dark:text-gray-400">
                                {conversation.user1.age} & {conversation.user2.age}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <p className="text-sm text-gray-900 dark:text-white max-w-xs truncate">
                            {conversation.lastMessage}
                          </p>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center space-x-2">
                            <MessageSquare className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-900 dark:text-white">{conversation.messageCount}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className={`flex items-center space-x-2 ${getStatusColor(conversation.status)}`}>
                            {getStatusIcon(conversation.status)}
                            <span className="text-sm font-medium capitalize">{conversation.status}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                          {conversation.lastActivity}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap relative">
                          <button
                            className="p-2 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 action-dropdown"
                            onClick={() => handleActionClick(conversation.id)}
                          >
                            <MoreVertical className="w-5 h-5" />
                          </button>
                          {actionDropdownId === conversation.id && (
                            <div className="absolute left-0 mt-2 z-50 w-40 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg action-dropdown">
                              <button className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700" onClick={closeDropdown}>
                                <Eye className="w-4 h-4 mr-2" /> View Conversation
                              </button>
                              <button className="w-full flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700" onClick={closeDropdown}>
                                <Flag className="w-4 h-4 mr-2" /> Flag
                              </button>
                              <button className="w-full flex items-center px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/20" onClick={closeDropdown}>
                                <Trash2 className="w-4 h-4 mr-2" /> Delete
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {/* Pagination Controls */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Showing {(currentPage - 1) * itemsPerPage + 1}
                -{Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} conversations
              </div>
              <div className="flex items-center gap-2">
                <button
                  className="px-3 py-1 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Prev
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    className={`px-3 py-1 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 ${
                      currentPage === page ? 'bg-pink-600 text-white dark:bg-pink-700' : ''
                    }`}
                    onClick={() => goToPage(page)}
                  >
                    {page}
                  </button>
                ))}
                <button
                  className="px-3 py-1 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Message Insights */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Message Topics</h3>
          <div className="space-y-4">
            {messageInsights.map((insight, index) => (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-gradient-to-r from-pink-500 to-purple-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
                    {index + 1}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">{insight.topic}</p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{insight.count} messages</p>
                  </div>
                </div>
                <span className="text-sm font-medium text-green-600 dark:text-green-400">{insight.trend}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Quick Actions</h3>
          <div className="space-y-3">
            <button className="w-full flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
              <span className="text-sm text-gray-900 dark:text-white">View All Messages</span>
              <ArrowUpRight className="w-4 h-4 text-gray-400" />
            </button>
            <button className="w-full flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
              <span className="text-sm text-gray-900 dark:text-white">Moderation Queue</span>
              <Shield className="w-4 h-4 text-gray-400" />
            </button>
            <button className="w-full flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
              <span className="text-sm text-gray-900 dark:text-white">Message Settings</span>
              <Settings className="w-4 h-4 text-gray-400" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Messages; 